import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";

// ── Types ───────────────────────────────────────────────────────────────

export interface Calendar {
  id: string;
  org_id: string;
  slug: string;
  name: string;
  color: string;
  timezone: string;
  default_reminder_recipients: any;
  visibility_rules: any;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CalendarEvent {
  id: string;
  org_id: string;
  calendar_id: string;
  title: string;
  event_type: "standard" | "out_of_office";
  client_or_brand: string | null;
  start_at: string;
  end_at: string;
  all_day: boolean;
  location_or_asset: string | null;
  owner_user_id: string | null;
  required_roles: string[];
  required_headcount: number | null;
  status: "planned" | "confirmed" | "cancelled";
  notes: string | null;
  recurrence: any;
  source: any;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateCalendarEventInput {
  calendar_id: string;
  title: string;
  event_type?: "standard" | "out_of_office";
  client_or_brand?: string;
  start_at: string;
  end_at: string;
  all_day?: boolean;
  owner_user_id?: string;
  required_roles?: string[];
  required_headcount?: number;
  status?: "planned" | "confirmed" | "cancelled";
  notes?: string;
}

export interface UpdateCalendarEventInput {
  calendar_id?: string;
  title?: string;
  event_type?: "standard" | "out_of_office";
  client_or_brand?: string | null;
  start_at?: string;
  end_at?: string;
  all_day?: boolean;
  owner_user_id?: string | null;
  required_roles?: string[];
  required_headcount?: number | null;
  status?: "planned" | "confirmed" | "cancelled";
  notes?: string | null;
}

export interface CalendarEventAttachment {
  id: string;
  event_id: string;
  org_id: string;
  uploaded_by: string | null;
  filename: string;
  storage_path: string;
  mime_type: string | null;
  file_size: number | null;
  created_at: string;
}

// ── Queries ─────────────────────────────────────────────────────────────

/** Fetch all active calendars for the current org. */
export function useCalendars() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  return useQuery({
    queryKey: ["calendars"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("calendar" as any)
        .select("*")
        .eq("is_active", true)
        .order("name", { ascending: true });

      if (error) throw error;
      return (data || []) as Calendar[];
    },
    enabled: isAuthenticated && !authLoading,
  });
}

/** Fetch calendar events in a date range, filtered by selected calendar IDs. */
export function useCalendarEvents(
  calendarIds: string[],
  startDate: string,
  endDate: string
) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  return useQuery({
    queryKey: ["calendar-events", calendarIds, startDate, endDate],
    queryFn: async () => {
      if (calendarIds.length === 0) return [];

      const { data, error } = await supabase
        .from("calendar_event" as any)
        .select("*")
        .in("calendar_id", calendarIds)
        .lte("start_at", endDate)
        .gte("end_at", startDate)
        .order("start_at", { ascending: true });

      if (error) throw error;
      return (data || []) as CalendarEvent[];
    },
    enabled: isAuthenticated && !authLoading && calendarIds.length > 0,
  });
}

/** Fetch a single calendar event by ID. */
export function useCalendarEvent(id: string | null) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  return useQuery({
    queryKey: ["calendar-events", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("calendar_event" as any)
        .select("*")
        .eq("id", id!)
        .single();

      if (error) throw error;
      return data as CalendarEvent;
    },
    enabled: !!id && isAuthenticated && !authLoading,
  });
}

// ── Mutations ───────────────────────────────────────────────────────────

/** Create a new calendar event. */
export function useCreateCalendarEvent() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateCalendarEventInput) => {
      const { data: userData, error: userError } = await supabase
        .from("user_account")
        .select("org_id, id")
        .eq("external_auth_user", (await supabase.auth.getUser()).data.user?.id)
        .single();

      if (userError || !userData) throw new Error("Failed to get user organization");

      const { data, error } = await supabase
        .from("calendar_event" as any)
        .insert([{
          calendar_id: input.calendar_id,
          title: input.title,
          event_type: input.event_type || "standard",
          client_or_brand: input.client_or_brand || null,
          start_at: input.start_at,
          end_at: input.end_at,
          all_day: input.all_day ?? false,
          owner_user_id: input.owner_user_id || null,
          required_roles: input.required_roles || [],
          required_headcount: input.required_headcount || null,
          status: input.status || "planned",
          notes: input.notes || null,
          org_id: userData.org_id,
          created_by: userData.id,
        }])
        .select()
        .single();

      if (error) throw error;
      return data as CalendarEvent;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["calendar-events"] });
      toast({
        title: "Event created",
        description: `"${data.title}" has been added to the calendar.`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error creating event",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

/** Update an existing calendar event. */
export function useUpdateCalendarEvent() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UpdateCalendarEventInput }) => {
      const { data, error } = await supabase
        .from("calendar_event" as any)
        .update(input)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data as CalendarEvent;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["calendar-events"] });
      toast({
        title: "Event updated",
        description: "The calendar event has been updated.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error updating event",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

/** Delete a calendar event. */
export function useDeleteCalendarEvent() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("calendar_event" as any)
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["calendar-events"] });
      toast({
        title: "Event deleted",
        description: "The calendar event has been removed.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error deleting event",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}
